import React, { useState } from 'react'
import { Loader2, Building2, Search, ArrowRight, Eye, EyeOff } from 'lucide-react'
import { CompanyJoinConfirmationModal } from './CompanyJoinConfirmationModal'
import { logger } from '../../utils/logger'

interface CompanyJoinFormProps {
  onBack: () => void
  onSuccess: (message: string) => void
  onError: (message: string) => void
  onLogin?: () => void
  onForgotPassword?: () => void
}

// Данные для поиска компании (Шаг 1)
interface CompanySearchData {
  company_iin: string
}

// Данные пользователя (Шаг 2)
interface UserFormData {
  name: string
  surname: string
  phone: string
  email: string
  password: string
  confirmPassword: string
}

// Информация о найденной компании
interface CompanyInfo {
  company_name: string
  full_name?: string
  status?: string
}

interface ContactInfo {
  id: string
  name: string
  email?: string
  phone?: string
  company?: string
}

interface CompanyData {
  ID: string
  TITLE: string
  EMAIL?: string
}

interface FindContactResponse {
  success: boolean
  found: boolean
  contact?: ContactInfo
  company_data?: CompanyData
  message?: string
  email_sent?: boolean
}

export const CompanyJoinForm: React.FC<CompanyJoinFormProps> = ({
  onBack,
  onSuccess,
  onError,
  onLogin,
  onForgotPassword
}) => {
  // Состояние для управления шагами
  const [currentStep, setCurrentStep] = useState<'search' | 'form' | 'found' | 'success'>('search')
  
  // Локальное состояние для ошибок
  const [localError, setLocalError] = useState<string>('')
  
  // Функция для очистки ошибок
  const clearErrors = () => {
    setLocalError('')
    setFieldErrors({})
  }

  // Проверка, что пользователь из мини-приложения Telegram
  const isTelegramMiniApp = () => {
    return !!(window as any).Telegram?.WebApp?.initDataUnsafe?.user?.id
  }

  // Форматирование номера телефона (как в RegistrationForm)
  const formatPhoneNumber = (value: string): string => {
    // Убираем все нецифровые символы
    const numbers = value.replace(/\D/g, '')
    
    // Если начинается с 8, заменяем на 7
    let formattedNumbers = numbers
    if (numbers.startsWith('8')) {
      formattedNumbers = '7' + numbers.slice(1)
    }
    
    // Если не начинается с 7, добавляем 7 в начало
    if (!formattedNumbers.startsWith('7') && formattedNumbers.length > 0) {
      formattedNumbers = '7' + formattedNumbers
    }
    
    // Ограничиваем длину (7 + 10 цифр = 11 максимум)
    formattedNumbers = formattedNumbers.slice(0, 11)
    
    // Форматируем как +7 (999) 123-45-67
    if (formattedNumbers.length === 0) return ''
    if (formattedNumbers.length === 1) return `+${formattedNumbers}`
    if (formattedNumbers.length <= 4) return `+${formattedNumbers.slice(0, 1)} (${formattedNumbers.slice(1)}`
    if (formattedNumbers.length <= 7) return `+${formattedNumbers.slice(0, 1)} (${formattedNumbers.slice(1, 4)}) ${formattedNumbers.slice(4)}`
    if (formattedNumbers.length <= 9) return `+${formattedNumbers.slice(0, 1)} (${formattedNumbers.slice(1, 4)}) ${formattedNumbers.slice(4, 7)}-${formattedNumbers.slice(7)}`
    
    return `+${formattedNumbers.slice(0, 1)} (${formattedNumbers.slice(1, 4)}) ${formattedNumbers.slice(4, 7)}-${formattedNumbers.slice(7, 9)}-${formattedNumbers.slice(9)}`
  }
  
  // Данные для поиска компании (Шаг 1)
  const [companySearchData, setCompanySearchData] = useState<CompanySearchData>({
    company_iin: ''
  })
  
  // Данные пользователя (Шаг 2)
  const [userFormData, setUserFormData] = useState<UserFormData>({
    name: '',
    surname: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  
  // Информация о найденной компании
  const [foundCompany, setFoundCompany] = useState<CompanyInfo | null>(null)
  
  const [isLoading, setIsLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  // Состояние для результатов поиска контакта
  const [searchResult, setSearchResult] = useState<FindContactResponse | null>(null)
  const [showContactFound, setShowContactFound] = useState(false)
  
  // Состояние для модального окна подтверждения (СЦЕНАРИЙ B)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  // Функции для работы с данными поиска компании
  const handleCompanySearchChange = (field: keyof CompanySearchData, value: string) => {
    let processedValue = value
    
    // Для поля ИНН разрешаем только цифры
    if (field === 'company_iin') {
      // Удаляем все символы кроме цифр
      processedValue = value.replace(/\D/g, '')
      // Ограничиваем длину до 12 символов (максимальная длина ИНН)
      processedValue = processedValue.slice(0, 12)
    }
    
    setCompanySearchData(prev => ({ ...prev, [field]: processedValue }))
    
    // Очищаем ошибки при изменении
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }))
    }
    if (localError) {
      setLocalError('')
    }
  }

  // Функция для прокрутки к активному полю
  const scrollToInput = (element: HTMLInputElement) => {
    setTimeout(() => {
      // Находим контейнер формы
      const formContainer = element.closest('.form-container') as HTMLElement
      if (formContainer) {
        // Получаем позицию элемента относительно контейнера
        const elementRect = element.getBoundingClientRect()
        const containerRect = formContainer.getBoundingClientRect()
        const relativeTop = elementRect.top - containerRect.top + formContainer.scrollTop
        
        // Прокручиваем контейнер так, чтобы поле было видно
        const targetScrollTop = relativeTop - (formContainer.clientHeight / 2) + (element.offsetHeight / 2)
        
        formContainer.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        })
      } else {
        // Fallback для обычной прокрутки
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        })
      }
    }, 300) // Задержка для появления клавиатуры
  }

  // Функции для работы с данными пользователя
  const handleUserFormChange = (field: keyof UserFormData, value: string) => {
    let processedValue = value
    
    // Специальная обработка для телефона
    if (field === 'phone') {
      processedValue = formatPhoneNumber(value)
    }
    
    setUserFormData(prev => ({ ...prev, [field]: processedValue }))
    
    // Очищаем ошибки при изменении
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }))
    }
    if (localError) {
      setLocalError('')
    }
  }

  // Валидация ИНН компании (Шаг 1)
  const validateCompanySearch = (): boolean => {
    const errors: Record<string, string> = {}

    if (!companySearchData.company_iin.trim()) {
      errors.company_iin = 'ИНН компании обязателен'
    } else if (!/^\d{10,12}$/.test(companySearchData.company_iin)) {
      errors.company_iin = 'ИНН должен содержать 10-12 цифр'
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Валидация данных пользователя (Шаг 2)
  const validateUserForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!userFormData.name.trim()) {
      errors.name = 'Имя обязательно'
    } else if (userFormData.name.trim().length < 2) {
      errors.name = 'Имя должно содержать минимум 2 символа'
    } else if (!/^[а-яёА-ЯЁa-zA-Z\s\-]+$/.test(userFormData.name.trim())) {
      errors.name = 'Имя может содержать только буквы, пробелы и дефисы'
    }

    if (!userFormData.surname.trim()) {
      errors.surname = 'Фамилия обязательна'
    } else if (userFormData.surname.trim().length < 2) {
      errors.surname = 'Фамилия должна содержать минимум 2 символа'
    } else if (!/^[а-яёА-ЯЁa-zA-Z\s\-]+$/.test(userFormData.surname.trim())) {
      errors.surname = 'Фамилия может содержать только буквы, пробелы и дефисы'
    }

    if (!userFormData.phone.trim()) {
      errors.phone = 'Телефон обязателен'
    } else {
      // Валидация формата телефона (для отформатированного номера)
      const cleanPhone = userFormData.phone.replace(/[^\d]/g, '')
      if (cleanPhone.length < 11 || !cleanPhone.startsWith('7')) {
        errors.phone = 'Некорректный формат телефона (например: +7 (999) 123-45-67)'
      }
    }

    if (!userFormData.email.trim()) {
      errors.email = 'Email обязателен'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userFormData.email)) {
      errors.email = 'Некорректный email'
    }

    if (!userFormData.password.trim()) {
      errors.password = 'Пароль обязателен'
    } else if (userFormData.password.length < 6) {
      errors.password = 'Пароль должен содержать минимум 6 символов'
    }

    if (!userFormData.confirmPassword.trim()) {
      errors.confirmPassword = 'Подтверждение пароля обязательно'
    } else if (userFormData.password !== userFormData.confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают'
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Поиск компании по ИНН (Шаг 1)
  const handleCompanySearch = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateCompanySearch()) {
      return
    }

    setIsLoading(true)
    setFieldErrors({})

    try {
      const response = await fetch('/api/web_auth/company-by-inn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ inn: companySearchData.company_iin })
      })

      const result = await response.json()

      if (result.success) {
        // Компания найдена - переходим к шагу 2
        setFoundCompany({
          company_name: result.company_name,
          full_name: result.full_name,
          status: result.status
        })
        clearErrors() // Очищаем ошибки при успешном переходе
        setCurrentStep('form')
      } else {
        setLocalError(result.error || 'Компания с указанным ИНН не найдена')
      }
    } catch (error: any) {
      setLocalError('Ошибка при поиске компании. Попробуйте позже.')
    } finally {
      setIsLoading(false)
    }
  }

  // Отправка формы пользователя (Шаг 2)
  const handleUserFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateUserForm()) {
      return
    }

    setIsLoading(true)
    setShowContactFound(false)

    try {
      // Получаем telegram_id из Telegram WebApp (если доступен)
      const telegramId = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString()

      const requestData = {
        name: userFormData.name.trim(),
        surname: userFormData.surname.trim(),
        phone: userFormData.phone.replace(/[^\d]/g, ''), // Отправляем только цифры для Битрикс
        email: userFormData.email.trim(),
        password: userFormData.password.trim(),
        company_iin: companySearchData.company_iin.trim(),
        telegram_id: telegramId
      }

      logger.log('📤 Отправляем запрос поиска контакта:', requestData)

      const response = await fetch('/api/web_auth/find-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      const result: FindContactResponse = await response.json()
      logger.log('📥 Ответ API поиска контакта:', result)

      setSearchResult(result)

      if (result.success) {
        if (result.found) {
          // СЦЕНАРИЙ A: Контакт найден - показываем экран "уже зарегистрирован"
          clearErrors() // Очищаем ошибки при успешном переходе
          setCurrentStep('found')
          setShowContactFound(true)
        } else {
          // СЦЕНАРИЙ B: Контакт не найден - показываем модальное окно подтверждения
          setShowConfirmationModal(true)
        }
      } else {
        setLocalError(result.message || 'Произошла ошибка при поиске контакта')
      }

    } catch (error) {
      logger.error('❌ Ошибка при поиске контакта:', error)
      setLocalError('Произошла ошибка при поиске. Попробуйте позже.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmSendRequest = async () => {
    if (!searchResult?.company_data) {
      setLocalError('Данные компании не найдены')
      return
    }

    setIsLoading(true)
    setShowConfirmationModal(false)

    try {
      const telegramId = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString()

      const requestData = {
        name: userFormData.name.trim(),
        surname: userFormData.surname.trim(),
        phone: userFormData.phone.replace(/[^\d]/g, ''), // Отправляем только цифры для Битрикс
        email: userFormData.email.trim(),
        password: userFormData.password.trim(),
        company_iin: companySearchData.company_iin.trim(),
        company_data: searchResult.company_data,
        telegram_id: telegramId,
        employee_email: userFormData.email.trim()
      }

      logger.log('📤 Отправляем заявку на регистрацию:', requestData)

      const response = await fetch('/api/web_auth/submit-registration-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      logger.log('📥 Ответ API заявки на регистрацию:', result)

      if (result.success) {
        // Переходим к экрану успешной отправки заявки
        clearErrors() // Очищаем ошибки при успешном переходе
        setCurrentStep('success')
      } else {
        setLocalError(result.message || 'Произошла ошибка при отправке заявки')
      }

    } catch (error) {
      logger.error('❌ Ошибка при отправке заявки:', error)
      setLocalError('Произошла ошибка при отправке заявки. Попробуйте позже.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelSendRequest = () => {
    setShowConfirmationModal(false)
  }

  // Функция для возврата к предыдущему шагу
  const handleBackToSearch = () => {
    setCurrentStep('search')
    setFoundCompany(null)
    clearErrors()
  }

  const handleBackToForm = () => {
    setCurrentStep('form')
    setShowContactFound(false)
    setSearchResult(null)
    clearErrors()
  }

  return (
    <>
      <style>{`
        input::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        input {
          color: #000000 !important;
        }
        
        /* Предотвращение зума на мобильных устройствах */
        input, select, textarea {
          font-size: 16px !important;
          touch-action: manipulation;
        }
        
        /* Отключение двойного нажатия для зума */
        * {
          touch-action: manipulation;
          user-select: none;
        }
        
        input, textarea {
          user-select: text;
        }
        
        /* Адаптация для мобильной клавиатуры */
        @media (max-width: 768px) {
          .form-container {
            max-height: 80vh;
            max-width: 95vw;
            margin: 0.5rem;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }
          
          /* Автоматическая прокрутка к активному полю */
          input:focus, textarea:focus {
            scroll-margin-top: 20px;
            scroll-margin-bottom: 20px;
          }
        }
        
        /* Предотвращение зума в iOS Safari */
        @media screen and (-webkit-min-device-pixel-ratio: 0) {
          input, select, textarea {
            font-size: 16px !important;
          }
        }
        
        /* Дополнительные стили для Telegram WebApp */
        @media (max-height: 600px) {
          .form-container {
            padding: 1rem;
            margin: 0.25rem;
            max-height: 85vh;
            max-width: 98vw;
          }
        }
        
        /* Стили для очень маленьких экранов */
        @media (max-height: 500px) {
          .form-container {
            max-height: 90vh;
            max-width: 98vw;
            padding: 0.75rem;
            margin: 0.1rem;
          }
        }
        
        /* Улучшение скролла */
        .form-container::-webkit-scrollbar {
          width: 4px;
        }
        
        .form-container::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .form-container::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 2px;
        }
        
        .form-container::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }
      `}</style>
      <div className="form-container" style={{
        width: '100%',
        maxWidth: '420px',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        padding: '1.5rem',
        transform: 'scale(1)',
        transition: 'transform 0.3s ease',
        maxHeight: '85vh',
        overflowY: 'auto',
        flex: 'none',
        margin: '1rem'
      }}>
          
          {/* ШАГ 1: Поиск компании по ИНН */}
          {currentStep === 'search' && (
            <div>
              {/* Отображение локальной ошибки */}
              {localError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{localError}</p>
                </div>
              )}
              
              <div className="text-center mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-2">
                  Найти компанию
                </h1>
                <p className="text-gray-600 text-sm">
                  Введите ИНН компании для поиска
                </p>
              </div>

              <form onSubmit={handleCompanySearch} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    ИНН компании <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={companySearchData.company_iin}
                    onChange={(e) => handleCompanySearchChange('company_iin', e.target.value)}
                    placeholder="1234567890"
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.875rem',
                      border: '2px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      transition: 'all 0.2s ease',
                      background: '#ffffff',
                      boxSizing: 'border-box',
                      outline: 'none',
                      color: '#000000'
                    }}
                    onFocus={(e) => {
                      scrollToInput(e.target as HTMLInputElement)
                      e.target.style.borderColor = '#3b82f6'
                      e.target.style.background = '#ffffff'
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
                      e.target.style.color = '#000000'
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db'
                      e.target.style.background = '#ffffff'
                      e.target.style.boxShadow = 'none'
                      e.target.style.color = '#000000'
                    }}
                    pattern="[0-9]{10,12}"
                    required
                  />
                  {fieldErrors.company_iin && (
                    <div className="form-error text-red-500 text-sm mt-1">
                      {fieldErrors.company_iin}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    padding: '0.6rem 1rem',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    background: isLoading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Поиск компании...
                    </>
                  ) : (
                    <>
                      <Search size={18} />
                      Найти компанию
                    </>
                  )}
                </button>
              </form>

              <div className="text-center mt-6">
                <button 
                  type="button" 
                  onClick={onBack}
                  className="text-gray-500 text-sm font-medium hover:text-purple-600 transition-colors duration-200"
                >
                  ← Назад к выбору
                </button>
              </div>
            </div>
          )}

          {/* ШАГ 2: Форма с данными пользователя */}
          {currentStep === 'form' && foundCompany && (
            <div>
              {/* Отображение локальной ошибки */}
              {localError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{localError}</p>
                </div>
              )}
              
              <div className="text-center mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-3">
                  Присоединиться к компании
                </h1>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <p className="text-blue-800 text-sm font-semibold">
                    🏢 {foundCompany.company_name}
                  </p>
                  <p className="text-blue-600 text-xs mt-1">
                    ИНН: {companySearchData.company_iin}
                  </p>
                </div>
              </div>

              <form onSubmit={handleUserFormSubmit} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Имя <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={userFormData.name}
                    onChange={(e) => handleUserFormChange('name', e.target.value)}
                    onFocus={(e) => scrollToInput(e.target as HTMLInputElement)}
                    placeholder="Иван"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 outline-none text-black bg-white text-sm"
                    required
                  />
                  {fieldErrors.name && (
                    <div className="form-error text-red-500 text-sm mt-1">
                      {fieldErrors.name}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Фамилия <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={userFormData.surname}
                    onChange={(e) => handleUserFormChange('surname', e.target.value)}
                    onFocus={(e) => scrollToInput(e.target as HTMLInputElement)}
                    placeholder="Петров"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 outline-none text-black bg-white text-sm"
                    required
                  />
                  {fieldErrors.surname && (
                    <div className="form-error text-red-500 text-sm mt-1">
                      {fieldErrors.surname}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Номер телефона <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={userFormData.phone}
                    onChange={(e) => handleUserFormChange('phone', e.target.value)}
                    onFocus={(e) => scrollToInput(e.target as HTMLInputElement)}
                    placeholder="+7 (999) 123-45-67"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 outline-none text-black bg-white text-sm"
                    required
                  />
                  {fieldErrors.phone && (
                    <div className="form-error text-red-500 text-sm mt-1">
                      {fieldErrors.phone}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Ваш email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={userFormData.email}
                    onChange={(e) => handleUserFormChange('email', e.target.value)}
                    onFocus={(e) => scrollToInput(e.target as HTMLInputElement)}
                    placeholder="ivan@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 outline-none text-black bg-white text-sm"
                    required
                  />
                  {fieldErrors.email && (
                    <div className="form-error text-red-500 text-sm mt-1">
                      {fieldErrors.email}
                    </div>
                  )}
                </div>

                {/* Информационное сообщение о паролях */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">ℹ</span>
                    </div>
                    <p className="text-xs text-blue-700 leading-tight">
                      <strong>Пароль для веб-входа.</strong> Активируется после одобрения заявки.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Пароль <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={userFormData.password}
                      onChange={(e) => handleUserFormChange('password', e.target.value)}
                      onFocus={(e) => scrollToInput(e.target as HTMLInputElement)}
                      placeholder="Минимум 6 символов"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 outline-none text-black bg-white text-sm"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <div className="form-error text-red-500 text-sm mt-1">
                      {fieldErrors.password}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Подтвердите пароль <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={userFormData.confirmPassword}
                      onChange={(e) => handleUserFormChange('confirmPassword', e.target.value)}
                      onFocus={(e) => scrollToInput(e.target as HTMLInputElement)}
                      placeholder="Повторите пароль"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 outline-none text-black bg-white text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && (
                    <div className="form-error text-red-500 text-sm mt-1">
                      {fieldErrors.confirmPassword}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    padding: '0.6rem 1rem',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    background: isLoading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Проверка...
                    </>
                  ) : (
                    <>
                      <ArrowRight size={18} />
                      Присоединиться
                    </>
                  )}
                </button>
              </form>

              <div className="text-center mt-4">
                <button 
                  type="button" 
                  onClick={handleBackToSearch}
                  className="text-gray-500 text-sm font-medium hover:text-purple-600 transition-colors duration-200"
                >
                  ← Изменить компанию
                </button>
              </div>
            </div>
          )}

          {/* ШАГ 3: Контакт найден - показываем блок "уже зарегистрирован" */}
          {currentStep === 'found' && showContactFound && searchResult?.contact && (
            <div>
              <div className="text-center mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-2">
                  Вы уже зарегистрированы!
                </h1>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                <div className="text-center">
                  <div className="mb-3">
                    <span className="text-3xl">✅</span>
                  </div>
                  <h3 className="text-lg font-bold text-green-800 mb-3">Контакт найден в системе</h3>
                  
                  <div className="bg-white border border-green-200 rounded-lg p-3 mb-4 text-sm">
                    <div className="text-green-700 space-y-1">
                      <p><strong>Имя:</strong> {searchResult.contact.name}</p>
                      <p><strong>Компания:</strong> {searchResult.contact.company}</p>
                      {searchResult.contact.email && (
                        <p><strong>Email:</strong> {searchResult.contact.email}</p>
                      )}
                    </div>
                  </div>

                  {/* Информационное сообщение для Telegram */}
                  {(window as any).Telegram?.WebApp && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm">
                      <p className="text-blue-700">
                        <strong>📱 Telegram аккаунт</strong><br />
                        После входа ваш Telegram будет автоматически привязан к аккаунту.
                      </p>
                    </div>
                  )}

                  {/* Кнопки действий */}
                  <div className="space-y-3">
                    <button
                      onClick={onLogin}
                      style={{
                        width: '100%',
                        padding: '0.6rem 1rem',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      🔐 Войти в систему
                    </button>
                    
                    <button
                      onClick={onForgotPassword}
                      style={{
                        width: '100%',
                        padding: '0.6rem 1rem',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      🔑 Забыл пароль
                    </button>
                  </div>
                </div>
              </div>

              <div className="text-center mt-4">
                <button 
                  type="button" 
                  onClick={handleBackToForm}
                  className="text-gray-500 text-sm font-medium hover:text-purple-600 transition-colors duration-200"
                >
                  ← Попробовать другие данные
                </button>
              </div>
            </div>
          )}

          {/* ШАГ 4: Успешная отправка заявки */}
          {currentStep === 'success' && (
            <div>
              {isTelegramMiniApp() ? (
                // Специальное окно для пользователей мини-приложения
                <div>
                  <div className="text-center mb-4">
                    <div className="mb-4">
                      <div 
                        style={{
                          width: '80px',
                          height: '80px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          fontSize: '2rem'
                        }}
                      >
                        ✅
                      </div>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-3">
                      Заявка отправлена!
                    </h1>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Ваша заявка на присоединение к компании <strong>{foundCompany?.title}</strong> успешно отправлена.
                    </p>
                  </div>

                  <div 
                    style={{
                      background: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '12px',
                      padding: '1rem',
                      marginBottom: '1.5rem'
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div style={{ color: '#0ea5e9', fontSize: '1.2rem' }}>📱</div>
                      <div>
                        <p className="text-sm text-gray-700 mb-2">
                          <strong>Что дальше?</strong>
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• Администратор получит уведомление на email</li>
                          <li>• После одобрения вы получите доступ к системе</li>
                          <li>• Вы можете проверить статус заявки в любое время</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div 
                    style={{
                      background: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '12px',
                      padding: '1rem',
                      marginBottom: '1.5rem'
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div style={{ color: '#856404', fontSize: '1.2rem' }}>⏳</div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800 mb-2">
                          Ожидайте уведомление об одобрении доступа
                        </p>
                        <p className="text-sm text-gray-600">
                          Вы получите push-уведомление в Telegram, как только администратор компании одобрит вашу заявку.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        // Закрыть мини-приложение
                        (window as any).Telegram?.WebApp?.close()
                      }}
                      style={{
                        width: '100%',
                        padding: '0.6rem 1rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      ✖️ Закрыть приложение
                    </button>
                  </div>
                </div>
              ) : (
                // Обычное окно для веб-пользователей
                <div>
                  <div className="text-center mb-4">
                    <div className="mb-4">
                      <div 
                        style={{
                          width: '80px',
                          height: '80px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          fontSize: '2rem'
                        }}
                      >
                        ✅
                      </div>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-3">
                      Заявка отправлена!
                    </h1>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Ваша заявка на присоединение к компании <strong>{foundCompany?.title}</strong> успешно отправлена.
                    </p>
                  </div>

                  <div 
                    style={{
                      background: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '12px',
                      padding: '1rem',
                      marginBottom: '1.5rem'
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div style={{ color: '#0ea5e9', fontSize: '1.2rem' }}>ℹ️</div>
                      <div>
                        <p className="text-sm text-gray-700 mb-2">
                          <strong>Что дальше?</strong>
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• Администратор компании получит уведомление на email</li>
                          <li>• После одобрения вы сможете войти в систему</li>
                          <li>• Обычно рассмотрение занимает 1-2 рабочих дня</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        // Возвращаемся к началу для новой попытки
                        setCurrentStep('search')
                        setCompanySearchData({ company_iin: '' })
                        setUserFormData({ name: '', surname: '', phone: '', email: '', password: '', confirmPassword: '' })
                        setFoundCompany(null)
                        setSearchResult(null)
                        setFieldErrors({})
                      }}
                      style={{
                        width: '100%',
                        padding: '0.6rem 1rem',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      🔍 Подать еще одну заявку
                    </button>

                    <button
                      onClick={onBack}
                      style={{
                        width: '100%',
                        padding: '0.6rem 1rem',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      ← Вернуться к выбору
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      {/* Модальное окно подтверждения для СЦЕНАРИЯ B */}
      {showConfirmationModal && searchResult?.company_data && (
        <CompanyJoinConfirmationModal
          isOpen={showConfirmationModal}
          onClose={handleCancelSendRequest}
          onConfirm={handleConfirmSendRequest}
          onCancel={handleCancelSendRequest}
          company={{
            id: searchResult.company_data.ID,
            title: searchResult.company_data.TITLE,
            email: searchResult.company_data.EMAIL,
            iin: companySearchData.company_iin
          }}
          userData={{
            name: userFormData.name,
            surname: userFormData.surname,
            phone: userFormData.phone,
            email: userFormData.email
          }}
          isLoading={isLoading}
        />
      )}
    </>
  )
}
